import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout, Button, Space, Flex, Tooltip, Typography, Grid, Dropdown, Modal } from "antd";
import type { MenuProps } from "antd";
import {
  ReloadOutlined,
  LeftOutlined,
  LogoutOutlined,
  PrinterOutlined,
  HomeOutlined,
  MenuOutlined,
  TranslationOutlined,
  CheckOutlined,
  ShopOutlined,
  UserOutlined,
  KeyOutlined,
} from "@ant-design/icons";

import { useTranslation } from "react-i18next";

import ErrorBoundary from "../layout/ErrorBoundary";
import SetLangButton from "../buttons/SetLangButton";
import { ChangePasswordModal } from "../../../features/users";

import { useAuth } from "../../contexts";
import { postFetch, API_URL } from "../../utils/Util";
import { useAntdTheme } from "../../hooks";

const { Header, Content } = Layout;
const { Text } = Typography;
const { useBreakpoint } = Grid;

interface PageProps {
  headerClassName?: string;
  title?: string;
  noBack?: boolean;
  noCard?: boolean;
  children: React.ReactNode;
}

const Page: React.FC<PageProps> = ({ title, noBack, noCard = false, children }) => {
  const navigate = useNavigate();
  const { token } = useAntdTheme();
  const screens = useBreakpoint();
  const { t, i18n } = useTranslation();
  const { authState, logout, hasMultipleRestaurants, hasManagerAccess } = useAuth();
  const [langModalOpen, setLangModalOpen] = useState(false);
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);

  // Check whether to reload to get new UI version every 5 minutes.
  useEffect(() => {
    if (import.meta.env.VITE_BUILD_TIME) {
      const buildTime = new Date(import.meta.env.VITE_BUILD_TIME);

      const interval = setInterval(
        () => {
          postFetch(
            `${API_URL}/v1/ui_version`,
            { build_time: buildTime },
            (resp: { version_ok?: boolean }) => {
              if (resp.version_ok !== undefined && resp.version_ok === false) {
                window.location.reload();
              }
            }
          );
        },
        5 * 60 * 1000
      );

      return () => {
        clearInterval(interval);
      };
    }
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const languages = [
    { code: "en", name: "English", nativeName: "English", flag: "🇺🇸" },
    { code: "zh", name: "Traditional Chinese", nativeName: "中文", flag: "🇹🇼" },
    { code: "es", name: "Spanish", nativeName: "Español", flag: "🇪🇸" },
  ];

  const handleLanguageSelect = (langCode: string) => {
    localStorage.setItem("user_pref:lang", langCode);
    i18n.changeLanguage(langCode);
    setLangModalOpen(false);
  };

  // Menu items for mobile hamburger menu
  const menuItems: MenuProps["items"] = [
    ...(hasMultipleRestaurants()
      ? [
          {
            key: "restaurant",
            label: authState.currentRestaurant?.name || t("switch_restaurant"),
            icon: <ShopOutlined />,
            onClick: () => navigate("/select-restaurant"),
          },
        ]
      : []),
    {
      key: "language",
      label: t("language"),
      icon: <TranslationOutlined />,
      onClick: () => setLangModalOpen(true),
    },
    {
      key: "printer",
      label: t("printer_management"),
      icon: <PrinterOutlined />,
      onClick: () => navigate("/printer-management"),
    },
    {
      key: "refresh",
      label: t("refresh"),
      icon: <ReloadOutlined />,
      onClick: () => window.location.reload(),
    },
    {
      type: "divider",
    },
    ...(hasManagerAccess()
      ? [
          {
            key: "change_password",
            label: t("change_password"),
            icon: <KeyOutlined />,
            onClick: () => setChangePasswordModalOpen(true),
          },
        ]
      : []),
    {
      key: "logout",
      label: t("logout"),
      icon: <LogoutOutlined />,
      danger: true,
      onClick: handleLogout,
    },
  ];

  // User dropdown menu for desktop
  const userMenuItems: MenuProps["items"] = [
    ...(hasManagerAccess()
      ? [
          {
            key: "change_password",
            label: t("change_password"),
            icon: <KeyOutlined />,
            onClick: () => setChangePasswordModalOpen(true),
          },
          {
            type: "divider" as const,
          },
        ]
      : []),
    {
      key: "logout",
      label: t("logout"),
      icon: <LogoutOutlined />,
      danger: true,
      onClick: handleLogout,
    },
  ];

  return (
    <Layout style={{ minHeight: "100vh", background: token.colorBgLayout }}>
      <Header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1020,
          width: "100%",
          padding: `${token.paddingMD}px ${token.paddingLG}px`,
          background: token.colorPrimary,
          boxShadow: token.boxShadow,
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <Flex justify="space-between" align="center" style={{ height: "100%" }}>
          {/* Left side: Logo + Title */}
          <Flex align="center" gap="middle">
            {!noBack ? (
              <Tooltip title="Back to Home">
                <Button
                  type="text"
                  icon={<LeftOutlined style={{ fontSize: token.fontSizeLG }} />}
                  onClick={() => navigate("/")}
                  style={{
                    color: token.colorTextLightSolid,
                    display: "flex",
                    alignItems: "center",
                  }}
                  className="header-back-button"
                />
              </Tooltip>
            ) : (
              <HomeOutlined
                style={{
                  fontSize: token.fontSizeHeading2,
                  color: token.colorTextLightSolid,
                  marginRight: token.marginXS,
                }}
              />
            )}
            <Flex align="center" gap="middle">
              {/* Logo text - only shown on large screens and above (≥992px) */}
              {screens.lg && (
                <Text
                  strong
                  style={{
                    color: token.colorTextLightSolid,
                    fontSize: token.fontSizeHeading2,
                    letterSpacing: "-0.5px",
                    cursor: noBack ? "default" : "pointer",
                  }}
                  onClick={() => !noBack && navigate("/")}
                >
                  TicketRails
                </Text>
              )}
              {/* Title - shown on all screens */}
              {title && (
                <>
                  {screens.lg && (
                    <Text
                      style={{
                        color: "rgba(255, 255, 255, 0.4)",
                        fontSize: token.fontSizeHeading4,
                        margin: `0 ${token.marginXXS}px`,
                      }}
                    >
                      /
                    </Text>
                  )}
                  <Text
                    style={{
                      color: token.colorTextLightSolid,
                      fontSize: token.fontSizeHeading4,
                      fontWeight: token.fontWeightStrong,
                    }}
                  >
                    {title}
                  </Text>
                </>
              )}
            </Flex>
          </Flex>

          {/* Right side: Action buttons */}
          {screens.sm ? (
            // Desktop & Horizontal Phone: Show all buttons
            <Space size="middle">
              {hasMultipleRestaurants() && (
                <Tooltip title={t("switch_restaurant")}>
                  <Button
                    icon={<ShopOutlined style={{ fontSize: token.fontSizeLG }} />}
                    onClick={() => navigate("/select-restaurant")}
                    size="large"
                    style={{
                      background: "rgba(255, 255, 255, 0.15)",
                      backdropFilter: "blur(10px)",
                      WebkitBackdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      color: token.colorTextLightSolid,
                      fontWeight: token.fontWeightStrong,
                    }}
                    className="header-action-button"
                  >
                    {authState.currentRestaurant?.name || authState.currentRestaurant?.id}
                  </Button>
                </Tooltip>
              )}
              <SetLangButton />
              <Tooltip title="Printer Management">
                <Button
                  type="primary"
                  size="large"
                  icon={<PrinterOutlined />}
                  onClick={() => navigate("/printer-management")}
                  style={{
                    background: "rgba(255, 255, 255, 0.15)",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    color: token.colorTextLightSolid,
                  }}
                  className="header-action-button"
                />
              </Tooltip>
              <Tooltip title="Refresh Page">
                <Button
                  type="primary"
                  size="large"
                  icon={<ReloadOutlined />}
                  onClick={() => window.location.reload()}
                  style={{
                    background: "rgba(255, 255, 255, 0.15)",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    color: token.colorTextLightSolid,
                  }}
                  className="header-action-button"
                />
              </Tooltip>
              <Dropdown menu={{ items: userMenuItems }} trigger={["click"]} placement="bottomRight">
                <Button
                  type="primary"
                  size="large"
                  icon={<UserOutlined />}
                  style={{
                    background: "rgba(255, 255, 255, 0.15)",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    color: token.colorTextLightSolid,
                  }}
                  className="header-action-button"
                />
              </Dropdown>
            </Space>
          ) : (
            // Mobile Portrait: Show hamburger menu
            <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
              <Button
                type="primary"
                size="large"
                icon={<MenuOutlined />}
                style={{
                  background: "rgba(255, 255, 255, 0.15)",
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  color: token.colorTextLightSolid,
                }}
              />
            </Dropdown>
          )}
        </Flex>
      </Header>

      <Content
        style={{
          padding: noCard ? token.paddingSM : token.paddingMD,
          width: "100%",
          minHeight: "calc(100vh - 72px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {noCard ? (
          <ErrorBoundary>{children}</ErrorBoundary>
        ) : (
          <div
            style={{
              background: token.colorBgContainer,
              borderRadius: token.borderRadiusLG,
              boxShadow: token.boxShadowTertiary,
              padding: token.paddingLG,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
            }}
          >
            <ErrorBoundary>{children}</ErrorBoundary>
          </div>
        )}
      </Content>

      <Modal
        title={
          <div style={{ textAlign: "center" }}>
            <TranslationOutlined
              style={{
                fontSize: token.fontSizeHeading1,
                color: token.colorPrimary,
                marginBottom: token.marginSM,
              }}
            />
            <Text
              strong
              style={{
                display: "block",
                fontSize: token.fontSizeHeading3,
                color: token.colorText,
                marginBottom: token.marginXS,
              }}
            >
              {t("choose_your_language")}
            </Text>
            <Text
              style={{
                display: "block",
                fontSize: token.fontSize,
                color: token.colorTextSecondary,
              }}
            >
              選擇您的語言 • Elige tu idioma
            </Text>
          </div>
        }
        open={langModalOpen}
        onCancel={() => setLangModalOpen(false)}
        footer={null}
        centered
        width={480}
        styles={{
          body: {
            padding: `${token.paddingLG}px ${token.paddingXL}px ${token.paddingXL}px`,
          },
        }}
      >
        <Flex vertical gap="middle">
          {languages.map((lang) => {
            const isSelected = i18n.language === lang.code;

            return (
              <button
                key={lang.code}
                onClick={() => handleLanguageSelect(lang.code)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  padding: `${token.paddingLG}px ${token.paddingXL}px`,
                  border: isSelected
                    ? `2px solid ${token.colorPrimary}`
                    : `2px solid ${token.colorBorder}`,
                  borderRadius: token.borderRadiusLG,
                  background: isSelected ? token.colorPrimaryBg : token.colorBgContainer,
                  cursor: "pointer",
                  transition: "all 0.3s",
                  outline: "none",
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = token.colorBgTextHover;
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = token.boxShadow;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = token.colorBgContainer;
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }
                }}
              >
                <Flex align="center" gap="middle">
                  <span style={{ fontSize: token.fontSizeHeading2, lineHeight: 1 }}>
                    {lang.flag}
                  </span>
                  <div style={{ textAlign: "left" }}>
                    <div
                      style={{
                        fontSize: token.fontSizeLG,
                        fontWeight: token.fontWeightStrong,
                        color: token.colorText,
                        marginBottom: "2px",
                      }}
                    >
                      {lang.nativeName}
                    </div>
                    <div
                      style={{
                        fontSize: token.fontSize,
                        color: token.colorTextSecondary,
                      }}
                    >
                      {lang.name}
                    </div>
                  </div>
                </Flex>

                {isSelected && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "28px",
                      height: "28px",
                      borderRadius: token.borderRadiusLG,
                      background: token.colorPrimary,
                      color: token.colorTextLightSolid,
                    }}
                  >
                    <CheckOutlined style={{ fontSize: token.fontSize }} />
                  </div>
                )}
              </button>
            );
          })}
        </Flex>

        <div
          style={{
            marginTop: token.marginLG,
            padding: token.paddingMD,
            background: token.colorBgTextHover,
            borderRadius: token.borderRadiusLG,
            textAlign: "center",
          }}
        >
          <Text
            style={{
              fontSize: token.fontSizeSM,
              color: token.colorTextTertiary,
            }}
          >
            {t("language_preference_saved")}
          </Text>
        </div>
      </Modal>

      <ChangePasswordModal
        open={changePasswordModalOpen}
        onClose={() => setChangePasswordModalOpen(false)}
        onLogout={handleLogout}
      />
    </Layout>
  );
};

export default Page;
